/**
 * FollowUpBoard — commercial follow-up panel for admin and marketing.
 *
 * Reads GET /leads/follow-ups on mount (and on manual reload). Renders four
 * buckets in the order the backend already returns (overdue → due_today →
 * upcoming → unscheduled) so no client-side re-sort is needed. Each card
 * exposes primary actions (Abrir lead / Preparar mensaje / Completada) and
 * a kebab menu (Posponer presets + custom / Descartar).
 *
 * Recommended-template lookup: the follow-ups payload does not include a
 * template hint (backend PR shipped it as a Python-only field). We load
 * `/message-templates?channel=whatsapp` once via useMessageTemplates and
 * resolve each card locally with `pickDefaultTemplateForStage`. If the
 * templates call fails, the board keeps working and "Preparar mensaje"
 * navigates to the lead without a `compose_template` param.
 *
 * All mutations refetch the whole payload — no optimistic UI. Simpler
 * mental model and impossible to leave the counts out of sync with the
 * items.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { leadComposeHref } from "@/lib/leadDetailAnchors";
import {
  ArrowClockwise, ArrowRight, CheckCircle, DotsThreeVertical, PaperPlaneTilt,
  WhatsappLogo, EnvelopeSimple, Phone, InstagramLogo, ChatText, Clock,
  ClockClockwise, EyeSlash, Warning, X, User as UserIcon,
} from "@phosphor-icons/react";
import { statusLabel } from "@/lib/leadStatuses";
import { useMessageTemplates } from "@/hooks/useMessageTemplates";
import { pickDefaultTemplateForStage } from "@/lib/messageTemplatesApi";
import {
  listFollowUps, completeFollowUp, snoozeFollowUp, dismissFollowUp,
  previewFollowUpBackfill, applyFollowUpBackfill,
} from "@/lib/followUpsApi";
import { isValidWhatsappPhone, normalizeWhatsappPhone } from "@/lib/bulkWhatsapp";
// El idioma de los seguimientos vive fuera desde que el Centro de acción
// enseña los mismos: una sola traducción de `first_contact` y `whatsapp`.
import {
  CHANNEL_LABEL, fmtAbsolute, humaniseActionType, relativeLabel,
} from "@/lib/followUpVocabulary";
import { formatError } from "@/lib/api";

const BUCKETS = ["overdue", "due_today", "upcoming", "unscheduled"];

const BUCKET_META = {
  overdue: {
    label: "Vencidas",
    empty: "No tienes seguimientos vencidos.",
    tone: "red",
  },
  due_today: {
    label: "Para hoy",
    empty: "No hay seguimientos pendientes para hoy.",
    tone: "amber",
  },
  upcoming: {
    label: "Próximas",
    empty: "No hay seguimientos próximos.",
    tone: "stone",
  },
  unscheduled: {
    label: "Sin seguimiento programado",
    empty: "Todos los leads activos tienen seguimiento programado.",
    tone: "stone",
  },
};

const TONE_STYLES = {
  red: {
    border: "border-l-red-500",
    dot: "bg-red-500",
    text: "text-red-700 dark:text-red-300",
  },
  amber: {
    border: "border-l-amber-500",
    dot: "bg-amber-500",
    text: "text-amber-700 dark:text-amber-300",
  },
  stone: {
    border: "border-l-stone-300 dark:border-l-stone-700",
    dot: "bg-stone-400",
    text: "text-stone-600 dark:text-stone-300",
  },
};

const CHANNEL_ICON = {
  whatsapp: WhatsappLogo,
  email: EnvelopeSimple,
  call: Phone,
  instagram: InstagramLogo,
  other: ChatText,
};

function isoIn(offsetMs) {
  return new Date(Date.now() + offsetMs).toISOString();
}

function isoTomorrowAt9() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
}

function toLocalDatetimeInputValue(dateObj) {
  const pad = (n) => String(n).padStart(2, "0");
  const y = dateObj.getFullYear();
  const m = pad(dateObj.getMonth() + 1);
  const d = pad(dateObj.getDate());
  const hh = pad(dateObj.getHours());
  const mm = pad(dateObj.getMinutes());
  return `${y}-${m}-${d}T${hh}:${mm}`;
}

// ---------------------------------------------------------------------------
// Snooze modal — custom datetime picker
// ---------------------------------------------------------------------------

function SnoozeCustomModal({ leadName, onCancel, onConfirm, busy }) {
  const defaultDate = new Date(Date.now() + 60 * 60 * 1000);
  const [value, setValue] = useState(toLocalDatetimeInputValue(defaultDate));
  const [error, setError] = useState("");

  const submit = () => {
    if (!value) { setError("Elige una fecha y hora."); return; }
    const dt = new Date(value);
    if (isNaN(dt.getTime())) { setError("Formato de fecha inválido."); return; }
    if (dt.getTime() <= Date.now()) { setError("Elige un momento futuro."); return; }
    onConfirm(dt.toISOString());
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="follow-up-snooze-modal"
    >
      <div className="bg-white dark:bg-stone-900 rounded-lg max-w-sm w-full shadow-2xl border border-stone-200 dark:border-stone-700">
        <div className="p-5 border-b border-stone-200 dark:border-stone-800 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 dark:text-stone-400">Posponer seguimiento</div>
            <h3 className="font-serif-dv text-2xl leading-tight text-stone-900 dark:text-stone-50">{leadName || "Lead"}</h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-stone-400 hover:text-stone-900 dark:hover:text-stone-100"
            data-testid="follow-up-snooze-modal-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-1 block">
              Fecha y hora
            </span>
            <input
              type="datetime-local"
              className="dv-input w-full"
              value={value}
              onChange={(e) => { setValue(e.target.value); setError(""); }}
              data-testid="follow-up-snooze-modal-input"
            />
          </label>
          {error && (
            <div className="text-xs text-red-700 dark:text-red-300" data-testid="follow-up-snooze-modal-error">
              {error}
            </div>
          )}
          <p className="text-[11px] text-stone-500 dark:text-stone-400">
            El seguimiento volverá a aparecer automáticamente en el bucket correspondiente cuando llegue esa hora.
          </p>
        </div>
        <div className="p-5 pt-0 flex gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="dv-btn dv-btn-ghost"
            disabled={busy}
            data-testid="follow-up-snooze-modal-cancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            className="dv-btn dv-btn-primary"
            disabled={busy}
            data-testid="follow-up-snooze-modal-confirm"
          >
            {busy ? "Guardando…" : "Posponer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Backfill preview modal — admin-only
// ---------------------------------------------------------------------------

function BackfillModal({ onClose, onApplied }) {
  const [phase, setPhase] = useState("idle"); // idle | previewing | previewed | applying | applied
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const runPreview = async () => {
    setPhase("previewing"); setError("");
    try {
      const data = await previewFollowUpBackfill();
      setPreview(data);
      setPhase("previewed");
    } catch (e) {
      setError(formatError(e));
      setPhase("idle");
    }
  };

  const runApply = async () => {
    const confirmed = window.confirm(
      "Esto programará seguimientos para leads existentes que todavía no tengan próxima acción. ¿Continuar?",
    );
    if (!confirmed) return;
    setPhase("applying"); setError("");
    try {
      const data = await applyFollowUpBackfill();
      setResult(data);
      setPhase("applied");
      onApplied?.();
    } catch (e) {
      setError(formatError(e));
      setPhase("previewed");
    }
  };

  useEffect(() => { runPreview(); /* eslint-disable-next-line */ }, []);

  const summary = preview || result;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="follow-up-backfill-modal"
    >
      <div className="bg-white dark:bg-stone-900 rounded-lg max-w-lg w-full shadow-2xl border border-stone-200 dark:border-stone-700 max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-stone-200 dark:border-stone-800 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 dark:text-stone-400">Admin</div>
            <h3 className="font-serif-dv text-2xl leading-tight text-stone-900 dark:text-stone-50">
              Inicializar seguimientos existentes
            </h3>
            <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">
              Programa la próxima acción para leads que aún no la tengan calculada. Idempotente: una segunda ejecución no toca nada.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-stone-400 hover:text-stone-900 dark:hover:text-stone-100"
            data-testid="follow-up-backfill-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {phase === "previewing" && (
            <div className="text-sm text-stone-500 dark:text-stone-400">Calculando…</div>
          )}

          {error && (
            <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded p-2">
              {error}
            </div>
          )}

          {summary && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <SummaryCell label="Candidatos" value={summary.total_candidates} />
              <SummaryCell label="A programar" value={summary.would_update} />
              <SummaryCell label="Vencidos" value={summary.would_be_overdue} />
              <SummaryCell label="Para hoy" value={summary.would_be_due_today} />
              <SummaryCell label="Próximos" value={summary.would_be_upcoming} />
              <SummaryCell label="Excluidos" value={summary.excluded_terminal} />
            </div>
          )}

          {summary?.sample?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-1">
                Muestra ({summary.sample.length})
              </div>
              <ul className="text-xs space-y-1 max-h-40 overflow-y-auto border border-stone-200 dark:border-stone-800 rounded p-2 bg-stone-50 dark:bg-stone-950">
                {summary.sample.map((s) => (
                  <li key={s.lead_id} className="flex justify-between gap-2">
                    <span className="truncate">{s.name || s.lead_id}</span>
                    <span className="text-stone-500 dark:text-stone-400 shrink-0">
                      {s.status} · {humaniseActionType(s.next_action_type)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {phase === "applied" && (
            <div className="text-sm text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded p-2">
              Inicialización aplicada. Los seguimientos ya están en el tablero.
            </div>
          )}
        </div>

        <div className="p-5 pt-0 flex gap-2 justify-end flex-wrap">
          <button
            type="button"
            onClick={onClose}
            className="dv-btn dv-btn-ghost"
            data-testid="follow-up-backfill-modal-cancel"
          >
            {phase === "applied" ? "Cerrar" : "Cancelar"}
          </button>
          {phase === "previewed" && (
            <button
              type="button"
              onClick={runApply}
              className="dv-btn dv-btn-primary"
              data-testid="follow-up-backfill-apply"
              disabled={preview?.would_update === 0}
              title={preview?.would_update === 0 ? "Nada que aplicar" : ""}
            >
              Aplicar inicialización
            </button>
          )}
          {phase === "applying" && (
            <button
              type="button"
              className="dv-btn dv-btn-primary"
              disabled
              data-testid="follow-up-backfill-applying"
            >
              Aplicando…
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCell({ label, value }) {
  return (
    <div className="border border-stone-200 dark:border-stone-800 rounded p-3 bg-stone-50 dark:bg-stone-950">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-1">
        {label}
      </div>
      <div className="font-serif-dv text-2xl leading-none text-stone-900 dark:text-stone-50">
        {value ?? 0}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

function FollowUpCard({
  item, now, recommendedTemplateName, recommendedTemplateId, tone, onMutation, setBanner,
}) {
  const [busy, setBusy] = useState(false);
  const [cardError, setCardError] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [snoozeCustomOpen, setSnoozeCustomOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const relTxt = relativeLabel(item.next_action_at, now);
  const absTxt = fmtAbsolute(item.next_action_at);
  const ChannelIcon = item.last_message_channel ? CHANNEL_ICON[item.last_message_channel] : null;
  // "#compose" lands on the message workflow (composer / suggested
  // messages) — with or without a recommended template.
  const composeHref = leadComposeHref(item.lead_id, recommendedTemplateId || null);

  const runMutation = useCallback(async (fn, successMsg) => {
    setBusy(true); setCardError("");
    try {
      await fn();
      setBanner?.({ tone: "ok", text: successMsg });
      await onMutation?.();
    } catch (e) {
      setCardError(formatError(e));
      setBanner?.({ tone: "error", text: formatError(e) });
    } finally {
      setBusy(false);
    }
  }, [onMutation, setBanner]);

  const doComplete = () => runMutation(
    () => completeFollowUp(item.lead_id),
    "Seguimiento completado.",
  );

  const doSnooze = (untilISO) => {
    setMenuOpen(false);
    setSnoozeCustomOpen(false);
    return runMutation(
      () => snoozeFollowUp(item.lead_id, untilISO),
      "Seguimiento pospuesto.",
    );
  };

  const doDismiss = () => {
    setMenuOpen(false);
    const confirmed = window.confirm("¿Descartar este seguimiento? El lead no se eliminará.");
    if (!confirmed) return;
    return runMutation(
      () => dismissFollowUp(item.lead_id),
      "Seguimiento descartado.",
    );
  };

  const toneStyle = TONE_STYLES[tone] || TONE_STYLES.stone;

  return (
    <>
      <div
        className={`dv-card border-l-4 ${toneStyle.border} space-y-2.5`}
        data-testid={`follow-up-card-${item.lead_id}`}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-stone-900 dark:text-stone-50 truncate">
                {item.name || "(sin nombre)"}
              </span>
              <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-300">
                {statusLabel(item.status) || item.status || "—"}
              </span>
            </div>
            <div className={`text-xs mt-1 ${toneStyle.text}`}>
              {humaniseActionType(item.next_action_type)}
              {absTxt && <> · {absTxt}</>}
              {relTxt && <> · <span className="font-medium">{relTxt}</span></>}
            </div>
          </div>
          <div className="relative shrink-0" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="min-h-[44px] min-w-[44px] p-2 rounded hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-500 dark:text-stone-400"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              disabled={busy}
              data-testid={`follow-up-card-${item.lead_id}-menu`}
            >
              <DotsThreeVertical size={20} weight="bold" />
            </button>
            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full mt-1 z-20 min-w-[220px] rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 shadow-lg py-1 text-sm"
                data-testid={`follow-up-card-${item.lead_id}-menu-open`}
              >
                <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500">
                  Posponer
                </div>
                <button
                  type="button"
                  onClick={() => doSnooze(isoIn(2 * 60 * 60 * 1000))}
                  className="w-full text-left px-3 py-2 hover:bg-stone-50 dark:hover:bg-stone-800 inline-flex items-center gap-2"
                  data-testid={`follow-up-card-${item.lead_id}-snooze-2h`}
                >
                  <Clock size={14} /> 2 horas
                </button>
                <button
                  type="button"
                  onClick={() => doSnooze(isoTomorrowAt9())}
                  className="w-full text-left px-3 py-2 hover:bg-stone-50 dark:hover:bg-stone-800 inline-flex items-center gap-2"
                  data-testid={`follow-up-card-${item.lead_id}-snooze-tomorrow`}
                >
                  <Clock size={14} /> Mañana 09:00
                </button>
                <button
                  type="button"
                  onClick={() => doSnooze(isoIn(3 * 24 * 60 * 60 * 1000))}
                  className="w-full text-left px-3 py-2 hover:bg-stone-50 dark:hover:bg-stone-800 inline-flex items-center gap-2"
                  data-testid={`follow-up-card-${item.lead_id}-snooze-3d`}
                >
                  <Clock size={14} /> 3 días
                </button>
                <button
                  type="button"
                  onClick={() => doSnooze(isoIn(7 * 24 * 60 * 60 * 1000))}
                  className="w-full text-left px-3 py-2 hover:bg-stone-50 dark:hover:bg-stone-800 inline-flex items-center gap-2"
                  data-testid={`follow-up-card-${item.lead_id}-snooze-1w`}
                >
                  <Clock size={14} /> 1 semana
                </button>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); setSnoozeCustomOpen(true); }}
                  className="w-full text-left px-3 py-2 hover:bg-stone-50 dark:hover:bg-stone-800 inline-flex items-center gap-2"
                  data-testid={`follow-up-card-${item.lead_id}-snooze-custom`}
                >
                  <ClockClockwise size={14} /> Elegir fecha y hora…
                </button>
                <div className="border-t border-stone-200 dark:border-stone-800 my-1" />
                <button
                  type="button"
                  onClick={doDismiss}
                  className="w-full text-left px-3 py-2 hover:bg-stone-50 dark:hover:bg-stone-800 inline-flex items-center gap-2 text-red-700 dark:text-red-300"
                  data-testid={`follow-up-card-${item.lead_id}-dismiss`}
                >
                  <EyeSlash size={14} /> Descartar seguimiento
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-y-1 gap-x-3 text-xs text-stone-500 dark:text-stone-400">
          <div>
            <span className="uppercase tracking-widest text-[10px] mr-1">Últ. interacción:</span>
            {item.last_message_sent_at ? (
              <span className="inline-flex items-center gap-1">
                {ChannelIcon && <ChannelIcon size={12} />}
                {CHANNEL_LABEL[item.last_message_channel] || "—"} · {fmtAbsolute(item.last_message_sent_at) || "—"}
              </span>
            ) : (
              <span>—</span>
            )}
          </div>
          <div>
            <span className="uppercase tracking-widest text-[10px] mr-1">Responsable:</span>
            <span className="inline-flex items-center gap-1">
              <UserIcon size={12} />
              {item.assigned_to_name || "Sin responsable"}
            </span>
          </div>
          {recommendedTemplateName && (
            <div className="sm:col-span-2">
              <span className="uppercase tracking-widest text-[10px] mr-1">Plantilla:</span>
              <span>{recommendedTemplateName}</span>
            </div>
          )}
        </div>

        {cardError && (
          <div
            className="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded p-2"
            data-testid={`follow-up-card-${item.lead_id}-error`}
          >
            {cardError}
          </div>
        )}

        <div className="flex flex-wrap gap-2 justify-end pt-1">
          <Link
            to={`/leads/${encodeURIComponent(item.lead_id)}`}
            className="text-xs min-h-[44px] px-3 rounded border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200 hover:border-stone-900 dark:hover:border-stone-300 inline-flex items-center gap-1"
            data-testid={`follow-up-card-${item.lead_id}-open`}
          >
            <ArrowRight size={12} /> Abrir lead
          </Link>
          {isValidWhatsappPhone(item.phone) && (
            <a
              href={`https://wa.me/${normalizeWhatsappPhone(item.phone)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs min-h-[44px] px-3 rounded border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200 hover:border-stone-900 dark:hover:border-stone-300 inline-flex items-center gap-1"
              data-testid={`follow-up-card-${item.lead_id}-whatsapp`}
            >
              <WhatsappLogo size={12} /> Abrir WhatsApp
            </a>
          )}
          <Link
            to={composeHref}
            className="text-xs min-h-[44px] px-3 rounded border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200 hover:border-stone-900 dark:hover:border-stone-300 inline-flex items-center gap-1"
            data-testid={`follow-up-card-${item.lead_id}-compose`}
          >
            <PaperPlaneTilt size={12} /> Preparar mensaje
          </Link>
          <button
            type="button"
            onClick={doComplete}
            disabled={busy}
            className="text-xs min-h-[44px] px-3 rounded bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50 inline-flex items-center gap-1"
            data-testid={`follow-up-card-${item.lead_id}-complete`}
          >
            <CheckCircle size={12} weight="bold" /> {busy ? "Guardando…" : "Completada"}
          </button>
        </div>
      </div>

      {snoozeCustomOpen && (
        <SnoozeCustomModal
          leadName={item.name}
          busy={busy}
          onCancel={() => setSnoozeCustomOpen(false)}
          onConfirm={(iso) => doSnooze(iso)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

function Bucket({ bucket, items, templates, now, onMutation, setBanner }) {
  const meta = BUCKET_META[bucket];
  const tone = meta.tone;
  const toneStyle = TONE_STYLES[tone] || TONE_STYLES.stone;

  return (
    <section className="mb-8" data-testid={`follow-up-bucket-${bucket}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`inline-block w-2 h-2 rounded-full ${toneStyle.dot}`} />
        <h3 className="text-sm uppercase tracking-[0.25em] text-stone-600 dark:text-stone-300">
          {meta.label} · <span data-testid={`follow-up-bucket-${bucket}-count`}>{items.length}</span>
        </h3>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-stone-400 dark:text-stone-500 italic px-1">{meta.empty}</div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const tpl = pickDefaultTemplateForStage(templates, it.status);
            return (
              <FollowUpCard
                key={it.lead_id}
                item={it}
                now={now}
                recommendedTemplateName={tpl?.name || null}
                recommendedTemplateId={tpl?.template_id || null}
                tone={tone}
                onMutation={onMutation}
                setBanner={setBanner}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Board
// ---------------------------------------------------------------------------

/**
 * FollowUpBoard — pass `showBackfill` from the parent to gate the admin
 * initialization CTA. Marketing never sees it; the backend also rejects
 * it with 403 even if the CTA were somehow reached.
 */
export default function FollowUpBoard({ showBackfill = false }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [banner, setBanner] = useState(null);
  const [now, setNow] = useState(() => new Date());
  const [backfillOpen, setBackfillOpen] = useState(false);
  const bannerTimerRef = useRef(null);

  const { templates } = useMessageTemplates({ channel: "whatsapp" });

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const data = await listFollowUps();
      setPayload(data);
      setNow(new Date());
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!banner) return undefined;
    if (bannerTimerRef.current) window.clearTimeout(bannerTimerRef.current);
    bannerTimerRef.current = window.setTimeout(() => setBanner(null), 3500);
    return () => {
      if (bannerTimerRef.current) window.clearTimeout(bannerTimerRef.current);
    };
  }, [banner]);

  const grouped = useMemo(() => {
    const g = { overdue: [], due_today: [], upcoming: [], unscheduled: [] };
    if (payload?.items) {
      for (const it of payload.items) {
        const b = it.bucket && g[it.bucket] ? it.bucket : "unscheduled";
        g[b].push(it);
      }
    }
    return g;
  }, [payload]);

  const counts = payload?.counts || { overdue: 0, due_today: 0, upcoming: 0, unscheduled: 0 };
  const badgeCount = (counts.overdue || 0) + (counts.due_today || 0);
  const totalItems = BUCKETS.reduce((acc, b) => acc + (grouped[b]?.length || 0), 0);
  const allEmpty = !loading && !error && totalItems === 0;

  return (
    <div data-testid="follow-up-board">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-serif-dv text-4xl leading-tight text-stone-900 dark:text-stone-50">
            Seguimientos comerciales
          </h1>
          <p className="text-stone-500 dark:text-stone-400 mt-1 text-sm">
            Acciones recomendadas según la fase del lead y el último mensaje confirmado.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {badgeCount > 0 && (
            <span
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
              data-testid="follow-up-badge-total"
            >
              <Warning size={12} weight="fill" /> {badgeCount} por atender
            </span>
          )}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="dv-btn dv-btn-ghost"
            data-testid="follow-up-reload"
            title="Recargar"
          >
            <ArrowClockwise size={16} className={loading ? "animate-spin" : ""} /> Recargar
          </button>
          {showBackfill && (
            <button
              type="button"
              onClick={() => setBackfillOpen(true)}
              className="dv-btn dv-btn-outline"
              data-testid="follow-up-backfill-open"
            >
              Inicializar seguimientos existentes
            </button>
          )}
        </div>
      </div>

      {/* Counters strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6" data-testid="follow-up-counts">
        {BUCKETS.map((b) => {
          const meta = BUCKET_META[b];
          const tone = TONE_STYLES[meta.tone] || TONE_STYLES.stone;
          return (
            <div
              key={b}
              className={`border border-stone-200 dark:border-stone-800 rounded-md px-3 py-2 bg-white dark:bg-stone-900 flex items-center gap-2`}
            >
              <span className={`inline-block w-2 h-2 rounded-full ${tone.dot}`} />
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest text-stone-500 dark:text-stone-400 truncate">
                  {meta.label}
                </div>
                <div
                  className="font-serif-dv text-2xl leading-none text-stone-900 dark:text-stone-50"
                  data-testid={`follow-up-count-${b}`}
                >
                  {counts[b] ?? 0}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {banner && (
        <div
          className={`mb-4 text-sm rounded px-3 py-2 border ${
            banner.tone === "error"
              ? "bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
              : "bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300"
          }`}
          data-testid="follow-up-banner"
          role="status"
        >
          {banner.text}
        </div>
      )}

      {loading && !payload && (
        <div className="space-y-3" data-testid="follow-up-skeleton">
          {[0, 1, 2].map((i) => (
            <div key={i} className="dv-card animate-pulse space-y-2">
              <div className="h-4 w-40 bg-stone-200 dark:bg-stone-800 rounded" />
              <div className="h-3 w-64 bg-stone-200 dark:bg-stone-800 rounded" />
              <div className="h-3 w-32 bg-stone-200 dark:bg-stone-800 rounded" />
            </div>
          ))}
        </div>
      )}

      {error && (
        <div
          className="dv-card text-center py-8"
          data-testid="follow-up-error"
        >
          <Warning size={28} weight="duotone" className="mx-auto text-red-500 mb-2" />
          <div className="text-sm text-stone-700 dark:text-stone-200 mb-3">
            No se pudieron cargar los seguimientos.
          </div>
          <div className="text-xs text-stone-500 dark:text-stone-400 mb-4">{error}</div>
          <button type="button" onClick={load} className="dv-btn dv-btn-primary" data-testid="follow-up-error-retry">
            Reintentar
          </button>
        </div>
      )}

      {!loading && !error && allEmpty && (
        <div className="dv-card text-center py-12" data-testid="follow-up-empty-global">
          <CheckCircle size={40} weight="light" className="mx-auto text-emerald-500 mb-3" />
          <div className="font-serif-dv text-2xl text-stone-900 dark:text-stone-50">Todo al día</div>
          <div className="text-stone-500 dark:text-stone-400 mt-1 text-sm">
            No hay seguimientos comerciales pendientes ahora mismo.
          </div>
        </div>
      )}

      {!error && !allEmpty && payload && BUCKETS.map((b) => (
        <Bucket
          key={b}
          bucket={b}
          items={grouped[b]}
          templates={templates}
          now={now}
          onMutation={load}
          setBanner={setBanner}
        />
      ))}

      {backfillOpen && (
        <BackfillModal
          onClose={() => setBackfillOpen(false)}
          onApplied={load}
        />
      )}
    </div>
  );
}
